import { Tooltip } from "react-tooltip";
import { MatchPlayerDamageModel, MatchPlayerModel } from "../../../../api/repositories/stats/models/matches";
import { localize } from "../../../../common/services/localization";
import { getValueOrDefault, getShortString, getMinutesOrDefault, isNothing, getColorByPlace, getRatingChange } from "../../../../common/utils";
import { HeroImage, TalentImage, ItemImage } from "../../ui/file-contents";
import { TableRows } from "../../ui/models/tables";
import { Table } from "../../ui/tables";
import { DarkTooltip } from "../../ui/other";
import { DamageTooltipBlock, ReceivedDamageTooltipBlock } from "./damage";
import { SubscribeGlobalState } from "../../../../common/hooks/global-state";
import { useState, useEffect } from "react";
import { HeroShared } from "../../../../api/repositories/cdn/models/shared";
import { SharedRepository } from "../../../../api/repositories/cdn/shared";

export const MatchPlayersTableBlock: React.FC<{
    matchPlayers: MatchPlayerModel[]
}> = ({ matchPlayers }) => {
    const language = SubscribeGlobalState('language');
    const heroes = SubscribeGlobalState('heroes');

    const defaultHeadColClassName = 'text-center font-normal'
    const defaultColClassName = 'w-[40px] text-center'

    const rows = matchPlayers.map((player, i) => ({
        className: `${i == matchPlayers.length - 1 ? '' : 'border-b-dark'}`,
        cols: [
            {
                value: getValueOrDefault(player.place),
                className: `${defaultColClassName}`
            },
            {
                value: <PlayerBlock player={player} />,
                className: `${defaultColClassName}`
            },
            {
                value: <HeroBlock player={player} heroes={heroes} />,
                className: `${defaultColClassName}`
            },
            {
                value: <TalentBlock heroName={player.heroName} talent={player.talents?.main} language={language} heroes={heroes} />,
                className: `${defaultColClassName}`
            },
            {
                value: <TalentBlock heroName={player.heroName} talent={player.talents?.secondMain} language={language} heroes={heroes} />,
                className: `${defaultColClassName}`
            },
            {
                value: <TalentsBlock player={player} />,
                className: `${defaultColClassName}`
            },
            {
                value: <KDABlock player={player} />,
                className: `${defaultColClassName}`
            },
            {
                value: getValueOrDefault(player.level),
                className: `${defaultColClassName}`
            },
            {
                value: getValueOrDefault(player.networth),
                className: `text-gold ${defaultColClassName}`
            },
            {
                value: getValueOrDefault(player.lastHits),
                className: `${defaultColClassName}`
            },
            {
                value: <AggregatedDamageBlock playerId={player.id} totalDamage={player.aggregatedDealtDamage?.total} type="dealt" />,
                className: `${defaultColClassName}`
            },
            {
                value: <AggregatedDamageBlock playerId={player.id} totalDamage={player.aggregatedReceivedDamage?.total} type="received" />,
                className: `${defaultColClassName}`
            },
            { value: getValueOrDefault(player.towerDamage), className: `${defaultColClassName}` },
            {
                value: <ItemsBlock items={player.items} />,
                className: `${defaultColClassName}`
            },
            {
                value: <RatingBlock player={player} />,
                className: `${defaultColClassName}`
            },
            { value: getMinutesOrDefault(player.endAtSeconds), className: `${defaultColClassName}` },
        ],
    })) as TableRows[];

    return <div className=" w-screen overflow-x-auto">
        <div className="w-[1561px]">
            <Table
                tableClassName="font-['Roboto'] "
                headClassName="text-[16px] "
                bodyClassName="text-[14px] font-normal"
                cols={[
                    { headValue: getHeaderCol("place", "match_players_table_place"), className: `pl-[24px] ${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("player", "match_players_table_player"), className: `w-[210px] ${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("hero", "match_players_table_hero"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("ability", "match_players_table_main_talent"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("second", "match_players_table_second_main_talent"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("talents", "match_players_table_talents"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("KDA", "match_players_table_kda"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("LVL", "match_players_table_level"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("NW", "match_players_table_networth"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("LH", "match_players_table_lasthits"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("HD", "match_players_table_dealt_damage"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("RD", "match_players_table_received_damage"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("TD", "match_players_table_tower_damage"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("items", "match_players_table_items"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("rating", "match_players_table_rating"), className: `${defaultHeadColClassName}` },
                    { headValue: getHeaderCol("end", "match_players_table_end_at"), className: `${defaultHeadColClassName}` },
                ]}
                rows={rows}
            />
            {
                matchPlayers.map((p, i) => <div>
                    <DarkTooltip key={i} id={`dealt-damage-${p.id}`}
                        content={<DamageTooltipBlock damage={p.aggregatedDealtDamage} />}
                    />
                    <DarkTooltip key={i} id={`received-damage-${p.id}`}
                        content={<ReceivedDamageTooltipBlock damage={p.aggregatedReceivedDamage} receivedDamages={p.receivedDamages} />}
                    />
                </div>
                )
            }
            <DarkTooltip id={`match`} />
            <DarkTooltip id={`talent`} />
        </div>
    </div>

    function getHeaderCol(name: string, tooltipLocalName: string) {
        return <div className="uppercase underline decoration-dotted underline-offset-4"
            data-tooltip-id='match'
            data-tooltip-html={localize(tooltipLocalName)}
        >
            {name}
        </div>
    }
}

const AggregatedDamageBlock: React.FC<{
    totalDamage?: number
    type: 'received' | 'dealt'
    playerId: number
}> = ({ totalDamage, type, playerId }) => {
    if (!totalDamage)
        return getValueOrDefault();

    return (
        <div
            className=""
            data-tooltip-id={`${type}-damage-${playerId}`}
        >
            <div className="uppercase underline decoration-dotted underline-offset-4">
                {totalDamage}
            </div>
        </div>
    );
};

const RatingBlock: React.FC<{
    player: MatchPlayerModel
}> = ({ player }) => {
    return <div className={`flex flex-col gap-[6px] justify-center ${getColorByPlace(player.place)}`} >
        <div>{player.ratingStart}</div>
        {player.ratingChange && <div>{getRatingChange(player.ratingChange, player.isDoubleRating)}</div>}
    </div >
}

const PlayerBlock: React.FC<{
    player: MatchPlayerModel
}> = ({ player }) => {
    return <a
        className="hover:brightness-[130%]"
        href={`/players/${player.id}`}>
        {getValueOrDefault(getShortString(player.name, 22))}
    </a>
}

const HeroBlock: React.FC<{
    player: MatchPlayerModel
    heroes?: Hero[]
}> = ({ player, heroes }) => {
    return player.heroName
        ? <a href={`/heroes/${heroes?.find(f => f.name == player.heroName)?.urlName}`}>
            <HeroImage className="flex h-[40px] mx-auto hover:brightness-[120%]" size="80x47" heroName={player.heroName} />
        </a>
        : <div className="h-[40px]">
            {getValueOrDefault()}
        </div>
}

const TalentBlock: React.FC<{
    heroes?: Hero[]
    language?: Language
    heroName?: string
    talent?: string
}> = ({ heroes, language, talent, heroName }) => {
    const hero = heroes?.find(f => f.name == heroName);

    const [heroShared, setHeroShared] = useState<HeroShared | undefined>();
    const [gotResponse, setGotResponse] = useState<boolean>(false);

    useEffect(() => {
        if (hero) {
            const repository = new SharedRepository();

            repository.getHero(hero.name, language ?? 'en')
                .then(t => setHeroShared(t))
                .then(t => setGotResponse(true));;
        }
    }, [hero?.name, language]);

    if (isNothing(talent) || isNothing(heroName) || talent == '')
        return getValueOrDefault()

    const skillNumber = (hero?.talents.findIndex(f => f == talent) ?? 0) + 1;

    return gotResponse && heroShared
        ? <TalentImage
            className="flex h-[40px] mx-auto"
            size="47x47"
            name={talent}
            dataTooltipId="talent"
            dataTooltipHtml={getTalentDescription()}
        />
        : <TalentImage
            className="flex h-[40px] mx-auto"
            size="47x47"
            name={talent}
        />

    function getTalentDescription(): string | undefined {
        const orangeTalent = heroShared?.orangeTalents[`skill${skillNumber}`];
        return orangeTalent && orangeTalent[0]?.description
    }
}



const ItemsBlock: React.FC<{
    items?: string[]
}> = ({ items }) => {
    if (isNothing(items) || (items?.length ?? 0) == 0)
        return <div>
            {getValueOrDefault()}
        </div>

    const filledItems = items ?? [];
    const totalItems = filledItems.length < 6
        ? [...filledItems, ...Array(6 - filledItems.length).fill("item_empty")]
        : filledItems;

    return (
        <div className="grid grid-cols-3 gap-[1px] h-[40px]">
            {totalItems.map((item, i) => (
                <div key={i}>
                    <ItemImage name={item} className="" />
                </div>
            ))}
        </div>
    );
};

const KDABlock: React.FC<{
    player: MatchPlayerModel
}> = ({ player }) => {
    return player.kills || player.assists || player.deaths
        ? <div className="flex flex-row gap-[2px] justify-center">
            <span className="text-green">{`${player.kills ?? 0}`}</span>
            <span className="text-gray">/</span>
            <span className="text-red">{`${player.deaths ?? 0}`}</span>
            <span className="text-gray">/</span>
            <span className="text-gray">{`${player.assists ?? 0}`}</span>
        </div>
        : getValueOrDefault()
}

const TalentsBlock: React.FC<{
    player: MatchPlayerModel
}> = ({ player }) => {
    return player.talents && (player.talents.purpleCount || player.talents.blueCount || player.talents.whiteCount)
        ? <div className="flex flex-row gap-[2px] justify-center">
            <span className="text-purple">{`${player.talents.purpleCount ?? 0}`}</span>
            <span className="text-gray">/</span>
            <span className="text-blue">{`${player.talents.blueCount ?? 0}`}</span>
            <span className="text-gray">/</span>
            <span className="">{`${player.talents.whiteCount ?? 0}`}</span>
        </div>
        : getValueOrDefault()
}